import type { Product } from "@/data/products";

/**
 * Filtros aplicáveis na sidebar da loja.
 * Mantemos a lógica isolada num único módulo para reutilização entre
 * Home, Categoria, Mais Vendidos e Coleções.
 */
export interface StoreFilters {
  category: string | null; // nome da categoria (legado) ou null
  priceRanges: string[]; // chaves dos intervalos selecionados
  sizes: string[]; // slugs/labels selecionados
  colors: string[]; // slugs/nomes selecionados
  bestsellersOnly: boolean;
  newOnly: boolean;
}

export const defaultFilters: StoreFilters = {
  category: null,
  priceRanges: [],
  sizes: [],
  colors: [],
  bestsellersOnly: false,
  newOnly: false,
};

export const PRICE_RANGES: Record<string, [number, number]> = {
  "Até R$30": [0, 30],
  "R$30–R$60": [30, 60],
  "R$60–R$100": [60, 100],
  "Acima de R$100": [100, Number.POSITIVE_INFINITY],
};

/**
 * Aplica filtros nos produtos. Recebe a lista "legacy" (usada na UI) e
 * opcionalmente a lista crua com relations (variants) — usada para filtrar
 * por tamanho/cor, que vivem nas variants.
 */
export const applyStoreFilters = (
  products: Product[],
  rawWithRelations: any[] | null,
  filters: StoreFilters,
): Product[] => {
  let result = [...products];

  if (filters.category) {
    result = result.filter((p) => p.category === filters.category);
  }

  if (filters.priceRanges.length) {
    result = result.filter((p) =>
      filters.priceRanges.some((key) => {
        const range = PRICE_RANGES[key];
        if (!range) return false;
        return p.price >= range[0] && p.price < range[1];
      }),
    );
  }

  if (filters.bestsellersOnly) {
    result = result.filter(
      (p) => p.is_bestseller || p.badge === "mais vendido",
    );
  }

  if (filters.newOnly) {
    result = result.filter((p) => p.badge === "novo");
  }

  if ((filters.sizes.length || filters.colors.length) && rawWithRelations) {
    const byId = new Map<string, any>(
      rawWithRelations.map((r) => [String(r.id), r]),
    );
    result = result.filter((p) => {
      const raw = byId.get(String(p.product_id ?? p.id));
      const variants: any[] = raw?.variants ?? [];
      if (!variants.length) return false;
      const sizeOk =
        !filters.sizes.length ||
        variants.some((v) =>
          filters.sizes.includes(v.size?.slug ?? v.size?.name ?? ""),
        );
      const colorOk =
        !filters.colors.length ||
        variants.some((v) =>
          filters.colors.includes(v.color?.slug ?? v.color?.name ?? ""),
        );
      return sizeOk && colorOk;
    });
  }

  return result;
};